import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
} from 'react'
import { useSession } from '@/context/AuthContext'
import type { Project, Task, TaskPriority, Subtask, SubSubtask, TaskStatus } from '@/lib/types'
import type { Visibility } from '@/lib/roles'
import { effectiveTaskStatus } from '@/lib/utils'
import { useToast } from '@/context/ToastContext'

// Cache "stale-while-revalidate" per-user di localStorage → paint instan saat
// buka app, lalu refresh di belakang. Di-namespace per email agar tak bocor
// antar akun di browser yang sama. Dibersihkan saat logout (lihat AppSidebar).
const CACHE_PREFIX = 'jal_cache_'
interface DataCache { projects: Project[]; tasks: Task[]; subtasks: Subtask[]; subSubtasks: SubSubtask[] }
function readCache(key: string | null): DataCache | null {
  if (!key || typeof window === 'undefined') return null
  try { const raw = localStorage.getItem(key); return raw ? (JSON.parse(raw) as DataCache) : null } catch { return null }
}
function writeCache(key: string | null, data: DataCache): void {
  if (!key || typeof window === 'undefined') return
  try { localStorage.setItem(key, JSON.stringify(data)) } catch { /* quota / private mode → abaikan */ }
}

// Ambil pesan error dari Response API (fallback ke pesan default).
async function readError(res: Response, fallback: string): Promise<string> {
  try {
    const body = await res.json()
    return body?.error || body?.reason || fallback
  } catch {
    return fallback
  }
}

interface AppContextValue {
  projects: Project[]
  tasks: Task[]
  subtasks: Subtask[]
  loading: boolean
  error: string | null
  createProject: (
    name: string, description: string, color: string, pic?: string,
    access?: { visibility?: Visibility; viewerEmails?: string[] },
  ) => Promise<Project>
  updateProject: (id: string, updates: Partial<Project>) => Promise<void>
  deleteProject: (id: string) => Promise<void>
  createTask: (projectId: string, title: string, description: string, priority: TaskPriority, assignee?: string | null, dueDate?: string | null) => Promise<Task>
  updateTask: (taskId: string, updates: Partial<Task> & { reason?: string }) => Promise<void>
  reorderTasks: (orderedIds: string[]) => Promise<void>
  deleteTask: (taskId: string) => Promise<void>
  getProjectTasks: (projectId: string) => Task[]
  getProject: (id: string) => Project | undefined
  getProjectLock: (projectId: string) => { locked: boolean; blocker: Project | null }
  getProjectOrder: (projectId: string) => number | null
  createSubtask: (taskId: string, title: string) => Promise<Subtask>
  toggleSubtask: (subtaskId: string, completed: boolean) => Promise<void>
  renameSubtask: (subtaskId: string, title: string) => Promise<void>
  setSubtaskOutput: (subtaskId: string, output: string) => Promise<void>
  setSubtaskDescription: (subtaskId: string, description: string) => Promise<void>
  setSubtaskDeadline: (subtaskId: string, deadline: string | null) => Promise<void>
  setSubtaskAssignee: (subtaskId: string, assignee: string | null) => Promise<void>
  setSubtaskRecurrence: (subtaskId: string, recurrence: Subtask['recurrence']) => Promise<void>
  deleteSubtask: (subtaskId: string) => Promise<void>
  getTaskSubtasks: (taskId: string) => Subtask[]
  subSubtasks: SubSubtask[]
  getSubSubtasks: (subtaskId: string) => SubSubtask[]
  createSubSubtask: (subtaskId: string, title: string) => Promise<SubSubtask>
  toggleSubSubtask: (subSubtaskId: string, completed: boolean) => Promise<void>
  renameSubSubtask: (subSubtaskId: string, title: string) => Promise<void>
  deleteSubSubtask: (subSubtaskId: string) => Promise<void>
  getEffectiveTaskStatus: (taskId: string) => TaskStatus
  assignPic: (projectId: string, assigneeEmail: string, assigneeName: string, reason?: string) => Promise<void>
  applyProject: (project: Project) => void
}

const AppContext = createContext<AppContextValue | null>(null)

export function AppProvider({ children }: { children: React.ReactNode }) {
  const [projects, setProjects] = useState<Project[]>([])
  const [tasks, setTasks] = useState<Task[]>([])
  const [subtasks, setSubtasks] = useState<Subtask[]>([])
  const [subSubtasks, setSubSubtasks] = useState<SubSubtask[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const { toast } = useToast()
  const { data: session } = useSession()
  // Key cache per-user (email). null saat belum login / email belum siap.
  const cacheKey = session?.user?.email ? `${CACHE_PREFIX}${session.user.email.toLowerCase()}` : null
  const cacheKeyRef = useRef(cacheKey)
  useEffect(() => { cacheKeyRef.current = cacheKey }, [cacheKey])

  // Ref agar callback selalu membaca data terbaru (untuk snapshot rollback)
  // tanpa membuat dependency callback berubah.
  const projectsRef = useRef(projects)
  const tasksRef = useRef(tasks)
  const subtasksRef = useRef(subtasks)
  const subSubtasksRef = useRef(subSubtasks)
  useEffect(() => { projectsRef.current = projects }, [projects])
  useEffect(() => { tasksRef.current = tasks }, [tasks])
  useEffect(() => { subtasksRef.current = subtasks }, [subtasks])
  useEffect(() => { subSubtasksRef.current = subSubtasks }, [subSubtasks])

  // Muat semua data dari Supabase. silent=true → tanpa loader/flash (untuk refetch realtime).
  const loadAll = useCallback(async (silent = false): Promise<void> => {
    try {
      const [pRes, tRes, sRes, ssRes] = await Promise.all([
        fetch('/api/projects'),
        fetch('/api/tasks'),
        fetch('/api/subtasks'),
        fetch('/api/subsubtasks'),
      ])

      if (!pRes.ok || !tRes.ok || !sRes.ok) {
        if (!silent) {
          const failed = [pRes, tRes, sRes].find((r) => !r.ok)!
          const errBody = await failed.json().catch(() => ({}))
          const msg = errBody?.reason || errBody?.error || 'API error'
          console.error('[AppContext] API error:', msg)
          setError(msg)
          toast(`Gagal memuat data: ${msg}`, 'error')
        }
        return
      }

      const [pData, tData, sData] = await Promise.all([pRes.json(), tRes.json(), sRes.json()])
      const projectsArr: Project[] = Array.isArray(pData) ? pData : []
      const tasksArr: Task[] = Array.isArray(tData) ? tData : []
      const subtasksArr: Subtask[] = Array.isArray(sData) ? sData : []
      setProjects(projectsArr)
      setTasks(tasksArr)
      setSubtasks(subtasksArr)
      let subSubArr: SubSubtask[] = subSubtasksRef.current
      if (ssRes.ok) {
        const ssData = await ssRes.json()
        subSubArr = Array.isArray(ssData) ? ssData : []
        setSubSubtasks(subSubArr)
      }
      // Simpan ke cache untuk paint instan di kunjungan berikutnya.
      writeCache(cacheKeyRef.current, { projects: projectsArr, tasks: tasksArr, subtasks: subtasksArr, subSubtasks: subSubArr })
      if (!silent) setError(null)
    } catch (err) {
      if (!silent) {
        console.error('[AppContext] Fetch failed:', String(err))
        setError(String(err))
        toast('Gagal memuat data. Periksa koneksi Anda.', 'error')
      }
    } finally {
      if (!silent) setLoading(false)
    }
  }, [toast])

  // Load awal (selalu jalan; menulis cache saat selesai).
  useEffect(() => { loadAll(false) }, [loadAll])

  // Paint instan dari cache begitu email user diketahui — hanya kalau state masih
  // kosong (jangan timpa data fresh yang mungkin sudah keburu masuk).
  const hydratedRef = useRef(false)
  useEffect(() => {
    if (hydratedRef.current || !cacheKey) return
    const cached = readCache(cacheKey)
    if (!cached) return
    hydratedRef.current = true
    setProjects((prev) => (prev.length ? prev : cached.projects ?? []))
    setTasks((prev) => (prev.length ? prev : cached.tasks ?? []))
    setSubtasks((prev) => (prev.length ? prev : cached.subtasks ?? []))
    setSubSubtasks((prev) => (prev.length ? prev : cached.subSubtasks ?? []))
    setLoading(false) // stale-while-revalidate: tampil cache dulu, fresh menyusul
  }, [cacheKey])

  // Real-time via SSE (Postgres NOTIFY → /api/realtime). Begitu ada perubahan DB
  // apa pun, server push event "change" → refetch (debounce). Plus refetch saat tab
  // kembali fokus, dan safety-net polling 60 detik kalau koneksi SSE sempat putus.
  useEffect(() => {
    const refetch = () => { void loadAll(true) }
    const onFocus = () => { if (document.visibilityState === 'visible') refetch() }
    window.addEventListener('focus', onFocus)
    document.addEventListener('visibilitychange', onFocus)

    let debounce: ReturnType<typeof setTimeout> | null = null
    const schedule = () => {
      if (debounce) clearTimeout(debounce)
      debounce = setTimeout(refetch, 300) // gabungkan perubahan beruntun
    }

    let es: EventSource | null = null
    try {
      es = new EventSource('/api/realtime') // cookie sesi otomatis ikut (same-origin)
      es.addEventListener('change', schedule)
      // Browser EventSource auto-reconnect saat koneksi putus (mis. fungsi 300s berakhir)
    } catch {
      /* SSE tak tersedia → andalkan polling/focus refetch di bawah */
    }

    const interval = setInterval(() => {
      if (document.visibilityState === 'visible') refetch()
    }, 60000)

    return () => {
      window.removeEventListener('focus', onFocus)
      document.removeEventListener('visibilitychange', onFocus)
      if (debounce) clearTimeout(debounce)
      clearInterval(interval)
      es?.close()
    }
  }, [loadAll])

  // Deteksi versi aplikasi baru (deploy). Tujuannya: pengguna selalu memakai
  // versi terbaru TANPA hard refresh manual, dan TANPA reload mendadak di depan
  // mata. Strategi: kalau ada versi baru, muat ulang hanya saat tab tidak terlihat
  // (berpindah tab) — pas kembali sudah versi terbaru, mulus.
  useEffect(() => {
    let initialVersion: string | null = null
    let updatePending = false

    const check = async () => {
      try {
        const res = await fetch('/api/version', { cache: 'no-store' })
        if (!res.ok) return
        const data = await res.json()
        const v: string | undefined = data?.version
        if (!v) return
        if (initialVersion === null) { initialVersion = v; return }
        if (v !== initialVersion && !updatePending) {
          updatePending = true
          toast('Versi baru tersedia — akan dimuat otomatis saat Anda berpindah tab.', 'info')
          if (document.visibilityState === 'hidden') window.location.reload()
        }
      } catch { /* abaikan */ }
    }

    const onVisibility = () => {
      if (document.visibilityState === 'visible') check()
      else if (updatePending) window.location.reload() // reload di latar, mulus
    }

    check()
    const interval = setInterval(check, 60_000)
    document.addEventListener('visibilitychange', onVisibility)
    window.addEventListener('focus', check)

    return () => {
      clearInterval(interval)
      document.removeEventListener('visibilitychange', onVisibility)
      window.removeEventListener('focus', check)
    }
  }, [toast])

  const createProject = useCallback(
    async (
      name: string, description: string, color: string, pic?: string,
      access?: { visibility?: Visibility; viewerEmails?: string[] },
    ): Promise<Project> => {
      const res = await fetch('/api/projects', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, description, color, pic, ...access }),
      })
      const data = await res.json()
      if (!res.ok || !data?.id) {
        throw new Error(data?.error ?? 'Gagal membuat project')
      }
      const project = data as Project
      // Project 'pending' (dibuat member) belum tampil di sistem — jangan masukkan
      // ke state. Hanya yang approved (mis. dibuat admin) yang langsung muncul.
      if (project.approvalStatus !== 'pending') {
        setProjects((prev) => [project, ...prev])
      }
      return project
    },
    []
  )

  const updateProject = useCallback(
    async (id: string, updates: Partial<Project>): Promise<void> => {
      const snapshot = projectsRef.current
      setProjects((prev) => prev.map((p) => (p.id === id ? { ...p, ...updates } : p)))
      try {
        const res = await fetch(`/api/projects/${id}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(updates),
        })
        if (!res.ok) throw new Error(await readError(res, 'Gagal menyimpan perubahan project'))
      } catch (err) {
        setProjects(snapshot) // rollback
        toast(err instanceof Error ? err.message : 'Gagal menyimpan perubahan project', 'error')
      }
    },
    [toast]
  )

  const deleteProject = useCallback(async (id: string): Promise<void> => {
    // Optimistic — hapus dari UI dulu, baru kirim ke server
    const projSnapshot = projectsRef.current
    const taskSnapshot = tasksRef.current
    setProjects((prev) => prev.filter((p) => p.id !== id))
    setTasks((prev) => prev.filter((t) => t.projectId !== id))
    try {
      const res = await fetch(`/api/projects/${id}`, { method: 'DELETE' })
      if (!res.ok) throw new Error(await readError(res, 'Gagal menghapus project'))
    } catch (err) {
      setProjects(projSnapshot) // rollback project + task terkait
      setTasks(taskSnapshot)
      toast(err instanceof Error ? err.message : 'Gagal menghapus project', 'error')
    }
  }, [toast])

  const createTask = useCallback(
    async (
      projectId: string,
      title: string,
      description: string,
      priority: TaskPriority,
      assignee: string | null = null,
      dueDate: string | null = null
    ): Promise<Task> => {
      const res = await fetch(`/api/projects/${projectId}/tasks`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ title, description, priority, assignee, dueDate }),
      })
      const data = await res.json()
      if (!res.ok || !data?.id) {
        throw new Error(data?.error ?? 'Gagal membuat task')
      }
      const task = data as Task
      setTasks((prev) => [task, ...prev])
      return task
    },
    []
  )

  const updateTask = useCallback(
    async (taskId: string, updates: Partial<Task> & { reason?: string }): Promise<void> => {
      // `reason` (alasan pemindahan) dikirim ke server tapi TIDAK di-merge ke state task.
      const { reason: _reason, ...patch } = updates
      void _reason
      // Optimistic — update UI langsung, sync ke server di background
      const snapshot = tasksRef.current
      setTasks((prev) =>
        prev.map((t) => (t.id === taskId ? { ...t, ...patch } : t))
      )
      try {
        const res = await fetch(`/api/tasks/${taskId}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(updates),
        })
        if (!res.ok) throw new Error(await readError(res, 'Gagal menyimpan perubahan task'))
      } catch (err) {
        setTasks(snapshot) // rollback
        toast(err instanceof Error ? err.message : 'Gagal menyimpan perubahan task', 'error')
      }
    },
    [toast]
  )

  // Simpan urutan manual task: sort_order = posisi di orderedIds (0,1,2,...).
  const reorderTasks = useCallback(async (orderedIds: string[]): Promise<void> => {
    const snapshot = tasksRef.current
    setTasks((prev) => prev.map((t) => {
      const idx = orderedIds.indexOf(t.id)
      return idx === -1 ? t : { ...t, sortOrder: idx }
    }))
    try {
      await Promise.all(orderedIds.map((id, idx) =>
        fetch(`/api/tasks/${id}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ sortOrder: idx }),
        }).then((r) => { if (!r.ok) throw new Error('gagal') }),
      ))
    } catch {
      setTasks(snapshot) // rollback
      toast('Gagal menyimpan urutan task', 'error')
    }
  }, [toast])

  const deleteTask = useCallback(async (taskId: string): Promise<void> => {
    // Optimistic — hapus dari UI dulu
    const snapshot = tasksRef.current
    setTasks((prev) => prev.filter((t) => t.id !== taskId))
    try {
      const res = await fetch(`/api/tasks/${taskId}`, { method: 'DELETE' })
      if (!res.ok) throw new Error(await readError(res, 'Gagal menghapus task'))
    } catch (err) {
      setTasks(snapshot) // rollback
      toast(err instanceof Error ? err.message : 'Gagal menghapus task', 'error')
    }
  }, [toast])

  const getProjectTasks = useCallback(
    (projectId: string): Task[] => {
      return tasks.filter((t) => t.projectId === projectId)
    },
    [tasks]
  )

  const getProject = useCallback(
    (id: string): Project | undefined => {
      return projects.find((p) => p.id === id)
    },
    [projects]
  )

  // Rantai 'Setelah' (ancestor) dari sebuah project, urut [terdekat ... akar]
  const getChain = useCallback(
    (projectId: string): Project[] => {
      const chain: Project[] = []
      const seen = new Set<string>([projectId])
      let curr = projects.find((x) => x.id === projectId)?.dependsOn ?? null
      while (curr && !seen.has(curr)) {
        seen.add(curr)
        const a = projects.find((x) => x.id === curr)
        if (!a) break
        chain.push(a)
        curr = a.dependsOn
      }
      return chain
    },
    [projects]
  )

  // Rangkaian project bersifat informatif saja (urutan pengerjaan), TIDAK mengunci.
  // Semua project tetap bisa diedit & diselesaikan kapan pun, walau project
  // sebelumnya di rantai belum 100% selesai — supaya fleksibel.
  const getProjectLock = useCallback(
    (_projectId: string): { locked: boolean; blocker: Project | null } => {
      return { locked: false, blocker: null }
    },
    []
  )

  // Posisi project dalam rantai (1 = pertama). null kalau tidak terhubung.
  const getProjectOrder = useCallback(
    (projectId: string): number | null => {
      const ancestors = getChain(projectId).length
      const hasDependents = projects.some((x) => x.dependsOn === projectId)
      if (ancestors === 0 && !hasDependents) return null
      return ancestors + 1
    },
    [getChain, projects]
  )

  // Sinkronkan tanggal_selesai TASK: terisi saat semua subtask selesai, kosong saat tidak
  const syncTaskDone = useCallback(async (taskId: string, newSubs: Subtask[]): Promise<void> => {
    // Subtask RUTIN dikecualikan: tidak ikut menentukan selesainya task induk.
    const taskSubs = newSubs.filter((s) => s.taskId === taskId && !s.recurrence)
    const done = taskSubs.length > 0 && taskSubs.every((s) => s.completed)
    const task = tasksRef.current.find((t) => t.id === taskId)
    if (!task) return
    const has = !!task.tanggalSelesai
    if (done === has) return
    const tanggalSelesai = done ? new Date().toISOString() : null
    const snapshot = tasksRef.current
    setTasks((prev) => prev.map((t) => (t.id === taskId ? { ...t, tanggalSelesai } : t)))
    try {
      const res = await fetch(`/api/tasks/${taskId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tanggalSelesai }),
      })
      if (!res.ok) throw new Error('sync gagal')
    } catch {
      setTasks(snapshot) // rollback status selesai task
    }
  }, [])

  const createSubtask = useCallback(
    async (taskId: string, title: string): Promise<Subtask> => {
      const res = await fetch(`/api/tasks/${taskId}/subtasks`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ title }),
      })
      if (!res.ok) {
        const msg = await readError(res, 'Gagal menambah subtask')
        toast(msg, 'error')
        throw new Error(msg)
      }
      const subtask: Subtask = await res.json()
      const newSubs = [...subtasksRef.current, subtask]
      setSubtasks(newSubs)
      await syncTaskDone(taskId, newSubs)
      return subtask
    },
    [syncTaskDone, toast]
  )

  const toggleSubtask = useCallback(
    async (subtaskId: string, completed: boolean): Promise<void> => {
      const prevSubs = subtasksRef.current
      const tanggalSelesai = completed ? new Date().toISOString() : null
      const newSubs = prevSubs.map((s) =>
        s.id === subtaskId ? { ...s, completed, tanggalSelesai } : s
      )
      setSubtasks(newSubs)
      try {
        const res = await fetch(`/api/subtasks/${subtaskId}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ completed, tanggalSelesai }),
        })
        if (!res.ok) throw new Error(await readError(res, 'Gagal memperbarui subtask'))
      } catch (err) {
        setSubtasks(prevSubs) // rollback
        toast(err instanceof Error ? err.message : 'Gagal memperbarui subtask', 'error')
        return
      }
      const sub = prevSubs.find((s) => s.id === subtaskId)
      if (sub) await syncTaskDone(sub.taskId, newSubs)
    },
    [syncTaskDone, toast]
  )

  const renameSubtask = useCallback(
    async (subtaskId: string, title: string): Promise<void> => {
      const snapshot = subtasksRef.current
      setSubtasks((prev) =>
        prev.map((s) => (s.id === subtaskId ? { ...s, title } : s))
      )
      try {
        const res = await fetch(`/api/subtasks/${subtaskId}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ title }),
        })
        if (!res.ok) throw new Error(await readError(res, 'Gagal mengganti nama subtask'))
      } catch (err) {
        setSubtasks(snapshot) // rollback
        toast(err instanceof Error ? err.message : 'Gagal mengganti nama subtask', 'error')
      }
    },
    [toast]
  )

  const setSubtaskOutput = useCallback(
    async (subtaskId: string, output: string): Promise<void> => {
      const snapshot = subtasksRef.current
      setSubtasks((prev) =>
        prev.map((s) => (s.id === subtaskId ? { ...s, output: output || null } : s))
      )
      try {
        const res = await fetch(`/api/subtasks/${subtaskId}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ output }),
        })
        if (!res.ok) throw new Error(await readError(res, 'Gagal menyimpan output subtask'))
      } catch (err) {
        setSubtasks(snapshot) // rollback
        toast(err instanceof Error ? err.message : 'Gagal menyimpan output subtask', 'error')
      }
    },
    [toast]
  )

  const setSubtaskDescription = useCallback(
    async (subtaskId: string, description: string): Promise<void> => {
      const snapshot = subtasksRef.current
      setSubtasks((prev) =>
        prev.map((s) => (s.id === subtaskId ? { ...s, description } : s))
      )
      try {
        const res = await fetch(`/api/subtasks/${subtaskId}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ description }),
        })
        if (!res.ok) throw new Error(await readError(res, 'Gagal menyimpan deskripsi subtask'))
      } catch (err) {
        setSubtasks(snapshot) // rollback
        toast(err instanceof Error ? err.message : 'Gagal menyimpan deskripsi subtask', 'error')
      }
    },
    [toast]
  )

  const setSubtaskDeadline = useCallback(
    async (subtaskId: string, deadline: string | null): Promise<void> => {
      const snapshot = subtasksRef.current
      setSubtasks((prev) =>
        prev.map((s) => (s.id === subtaskId ? { ...s, deadline } : s))
      )
      try {
        const res = await fetch(`/api/subtasks/${subtaskId}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ deadline }),
        })
        if (!res.ok) throw new Error(await readError(res, 'Gagal menyimpan deadline subtask'))
      } catch (err) {
        setSubtasks(snapshot) // rollback
        toast(err instanceof Error ? err.message : 'Gagal menyimpan deadline subtask', 'error')
      }
    },
    [toast]
  )

  const setSubtaskAssignee = useCallback(
    async (subtaskId: string, assignee: string | null): Promise<void> => {
      const snapshot = subtasksRef.current
      setSubtasks((prev) =>
        prev.map((s) => (s.id === subtaskId ? { ...s, assignee } : s))
      )
      try {
        const res = await fetch(`/api/subtasks/${subtaskId}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ assignee }),
        })
        if (!res.ok) throw new Error(await readError(res, 'Gagal menyimpan PIC subtask'))
      } catch (err) {
        setSubtasks(snapshot) // rollback
        toast(err instanceof Error ? err.message : 'Gagal menyimpan PIC subtask', 'error')
      }
    },
    [toast]
  )

  // Tandai subtask rutin (daily/weekly/monthly) atau lepas (null). Subtask rutin
  // keluar dari perhitungan selesai task → sinkronkan ulang status task induk.
  const setSubtaskRecurrence = useCallback(
    async (subtaskId: string, recurrence: Subtask['recurrence']): Promise<void> => {
      const prevSubs = subtasksRef.current
      const newSubs = prevSubs.map((s) => (s.id === subtaskId ? { ...s, recurrence } : s))
      setSubtasks(newSubs)
      try {
        const res = await fetch(`/api/subtasks/${subtaskId}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ recurrence: recurrence ?? null }),
        })
        if (!res.ok) throw new Error(await readError(res, 'Gagal menyimpan pengulangan subtask'))
      } catch (err) {
        setSubtasks(prevSubs) // rollback
        toast(err instanceof Error ? err.message : 'Gagal menyimpan pengulangan subtask', 'error')
        return
      }
      const sub = prevSubs.find((s) => s.id === subtaskId)
      if (sub) await syncTaskDone(sub.taskId, newSubs)
    },
    [syncTaskDone, toast]
  )

  const deleteSubtask = useCallback(async (subtaskId: string): Promise<void> => {
    const prevSubs = subtasksRef.current
    const sub = prevSubs.find((s) => s.id === subtaskId)
    const newSubs = prevSubs.filter((s) => s.id !== subtaskId)
    setSubtasks(newSubs)
    try {
      const res = await fetch(`/api/subtasks/${subtaskId}`, { method: 'DELETE' })
      if (!res.ok) throw new Error(await readError(res, 'Gagal menghapus subtask'))
    } catch (err) {
      setSubtasks(prevSubs) // rollback
      toast(err instanceof Error ? err.message : 'Gagal menghapus subtask', 'error')
      return
    }
    if (sub) await syncTaskDone(sub.taskId, newSubs)
  }, [syncTaskDone, toast])

  const getTaskSubtasks = useCallback(
    (taskId: string): Subtask[] => {
      return subtasks.filter((s) => s.taskId === taskId)
    },
    [subtasks]
  )

  const getSubSubtasks = useCallback(
    (subtaskId: string): SubSubtask[] => {
      return subSubtasks.filter((ss) => ss.subtaskId === subtaskId)
    },
    [subSubtasks]
  )

  // Sinkronkan completed SUBTASK induk dari anak-anaknya: subtask yang PUNYA
  // sub-subtask → completed = semua sub-subtask selesai. (Sumber kebenaran tetap
  // subtask.completed; ini menjaga roll-up ke task & progress otomatis konsisten.)
  const syncSubtaskFromChildren = useCallback(
    (subtaskId: string, children: SubSubtask[]): void => {
      const kids = children.filter((c) => c.subtaskId === subtaskId)
      if (kids.length === 0) return // tanpa anak → biarkan manual
      const done = kids.every((k) => k.completed)
      const sub = subtasksRef.current.find((s) => s.id === subtaskId)
      if (sub && sub.completed !== done) toggleSubtask(subtaskId, done)
    },
    [toggleSubtask]
  )

  const createSubSubtask = useCallback(
    async (subtaskId: string, title: string): Promise<SubSubtask> => {
      const res = await fetch(`/api/subtasks/${subtaskId}/subsubtasks`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ title }),
      })
      if (!res.ok) {
        const msg = await readError(res, 'Gagal menambah sub-subtask')
        toast(msg, 'error')
        throw new Error(msg)
      }
      const ss: SubSubtask = await res.json()
      const next = [...subSubtasksRef.current, ss]
      setSubSubtasks(next)
      syncSubtaskFromChildren(subtaskId, next)
      return ss
    },
    [syncSubtaskFromChildren, toast]
  )

  const toggleSubSubtask = useCallback(
    async (subSubtaskId: string, completed: boolean): Promise<void> => {
      const prev = subSubtasksRef.current
      const next = prev.map((ss) => (ss.id === subSubtaskId ? { ...ss, completed } : ss))
      setSubSubtasks(next)
      try {
        const res = await fetch(`/api/subsubtasks/${subSubtaskId}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ completed }),
        })
        if (!res.ok) throw new Error(await readError(res, 'Gagal memperbarui sub-subtask'))
      } catch (err) {
        setSubSubtasks(prev) // rollback
        toast(err instanceof Error ? err.message : 'Gagal memperbarui sub-subtask', 'error')
        return
      }
      const ss = prev.find((x) => x.id === subSubtaskId)
      if (ss) syncSubtaskFromChildren(ss.subtaskId, next)
    },
    [syncSubtaskFromChildren, toast]
  )

  const renameSubSubtask = useCallback(
    async (subSubtaskId: string, title: string): Promise<void> => {
      const snapshot = subSubtasksRef.current
      setSubSubtasks((prev) => prev.map((ss) => (ss.id === subSubtaskId ? { ...ss, title } : ss)))
      try {
        const res = await fetch(`/api/subsubtasks/${subSubtaskId}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ title }),
        })
        if (!res.ok) throw new Error(await readError(res, 'Gagal mengganti nama sub-subtask'))
      } catch (err) {
        setSubSubtasks(snapshot) // rollback
        toast(err instanceof Error ? err.message : 'Gagal mengganti nama sub-subtask', 'error')
      }
    },
    [toast]
  )

  const deleteSubSubtask = useCallback(
    async (subSubtaskId: string): Promise<void> => {
      const prev = subSubtasksRef.current
      const ss = prev.find((x) => x.id === subSubtaskId)
      const next = prev.filter((x) => x.id !== subSubtaskId)
      setSubSubtasks(next)
      try {
        const res = await fetch(`/api/subsubtasks/${subSubtaskId}`, { method: 'DELETE' })
        if (!res.ok) throw new Error(await readError(res, 'Gagal menghapus sub-subtask'))
      } catch (err) {
        setSubSubtasks(prev) // rollback
        toast(err instanceof Error ? err.message : 'Gagal menghapus sub-subtask', 'error')
        return
      }
      if (ss) syncSubtaskFromChildren(ss.subtaskId, next)
    },
    [syncSubtaskFromChildren, toast]
  )

  const getEffectiveTaskStatus = useCallback(
    (taskId: string): TaskStatus => {
      const task = tasks.find((t) => t.id === taskId)
      return effectiveTaskStatus(task?.status ?? 'not-started', subtasks.filter((s) => s.taskId === taskId))
    },
    [tasks, subtasks]
  )

  const applyProject = useCallback((project: Project): void => {
    setProjects((prev) => prev.map((p) => (p.id === project.id ? project : p)))
  }, [])

  const assignPic = useCallback(
    async (projectId: string, assigneeEmail: string, assigneeName: string, reason?: string): Promise<void> => {
      const res = await fetch(`/api/projects/${projectId}/assign`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ assigneeEmail, assigneeName, reason }),
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data?.error ?? 'Gagal assign PIC')
      if (data.project) applyProject(data.project as Project)
    },
    [applyProject],
  )

  return (
    <AppContext.Provider
      value={{
        projects,
        tasks,
        subtasks,
        loading,
        error,
        createProject,
        updateProject,
        deleteProject,
        createTask,
        updateTask,
        reorderTasks,
        deleteTask,
        getProjectTasks,
        getProject,
        getProjectLock,
        getProjectOrder,
        createSubtask,
        toggleSubtask,
        renameSubtask,
        setSubtaskOutput,
        setSubtaskDescription,
        setSubtaskDeadline,
        setSubtaskAssignee,
        setSubtaskRecurrence,
        deleteSubtask,
        getTaskSubtasks,
        subSubtasks,
        getSubSubtasks,
        createSubSubtask,
        toggleSubSubtask,
        renameSubSubtask,
        deleteSubSubtask,
        getEffectiveTaskStatus,
        assignPic,
        applyProject,
      }}
    >
      {children}
    </AppContext.Provider>
  )
}

export function useApp() {
  const ctx = useContext(AppContext)
  if (!ctx) throw new Error('useApp must be used within AppProvider')
  return ctx
}
